import { Controller } from "@hotwired/stimulus"

// Client combobox of block 1 of the estimate editor (`.combo` of the mockup): searches customers as the user types
// (their recent customers while empty), keyboard navigation, then the chosen customer's card and addresses. Its
// last option, and the link under the field, open the "Nouveau client" modal, whose customer is chosen once
// created. Options, cards and the modal form come from ERP::Sales::EstimateCustomersController. A choice is
// announced with erp--customer-combobox:selected.
// Connects to data-controller="erp--customer-combobox"
export default class extends Controller {
  static targets = [
    "input", "list", "combo", "hint", "picked", "customerId", "billing", "shipping",
    "createForm", "createName", "createError", "createSubmit"
  ]

  static values = { url: String, dialogId: String, mainAddressLabel: String }

  connect() {
    this._active = -1
    if (this.hasCreateFormTarget) this._blankCreateForm = this.createFormTarget.outerHTML
  }

  disconnect() {
    clearTimeout(this._searchTimeout)
    clearTimeout(this._closeTimeout)
    this._abort?.abort()
  }

  open() {
    clearTimeout(this._closeTimeout)
    this.#fetchOptions()
  }

  search() {
    clearTimeout(this._searchTimeout)
    this._searchTimeout = setTimeout(() => this.#fetchOptions(), 200)
  }

  // Delayed so a click on an option lands first. Leaving the field empty keeps the customer already chosen.
  close() {
    clearTimeout(this._closeTimeout)
    this._closeTimeout = setTimeout(() => {
      this.#closeList()
      if (!this.inputTarget.value.trim() && this.customerIdTarget.value) this.#showCard(true)
    }, 150)
  }

  // A mousedown on the list would blur the field and close the list before the click
  keepFocus(event) {
    event.preventDefault()
  }

  navigate(event) {
    const options = this.#options()

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (this.listTarget.hidden) return this.#fetchOptions()
        this.#activate((this._active + 1) % options.length)
        break
      case "ArrowUp":
        event.preventDefault()
        this.#activate((this._active - 1 + options.length) % options.length)
        break
      case "Enter":
        // Never submits the estimate form
        event.preventDefault()
        if (this._active >= 0) this.#choose(options[this._active])
        break
      case "Escape":
        this.#closeList()
        if (this.customerIdTarget.value) this.#showCard(true)
        break
    }
  }

  pick(event) {
    const option = event.target.closest("[role='option']")
    if (option) this.#choose(option)
  }

  // "Changer" on the card: back to the search, the current customer stays until another is chosen
  change() {
    this.#showCard(false)
    this.inputTarget.value = ""
    this.inputTarget.focus()
  }

  openCreate(event) {
    event?.preventDefault()
    this.#openCreate(this.inputTarget.value.trim())
  }

  async create(event) {
    event.preventDefault()
    const form = event.target
    this.createErrorTarget.hidden = true
    this.createSubmitTarget.disabled = true

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "text/html" }
      })
      const html = await response.text()

      if (response.status === 201) {
        this.#dialog()?.close()
        form.outerHTML = this._blankCreateForm
        this.#choose(this.#parseOption(html))
      } else if (response.status === 422) {
        form.outerHTML = html
        const invalid = this.createFormTarget.querySelector("[aria-invalid='true']")
        ;(invalid || this.createNameTarget).focus()
      } else {
        this.createErrorTarget.hidden = false
      }
    } catch (_error) {
      this.createErrorTarget.hidden = false
    } finally {
      this.createSubmitTarget.disabled = false
    }
  }

  async #fetchOptions() {
    this._abort?.abort()
    this._abort = new AbortController()

    const query = this.inputTarget.value.trim()
    const url = new URL(this.urlValue, window.location.origin)
    if (query) url.searchParams.set("q", query)

    try {
      const response = await fetch(url, { headers: { Accept: "text/html" }, signal: this._abort.signal })
      if (!response.ok) return

      this.listTarget.innerHTML = await response.text()
      this.listTarget.hidden = false
      this.inputTarget.setAttribute("aria-expanded", "true")
      this.#activate(query ? 0 : -1)
    } catch (error) {
      if (error.name !== "AbortError") throw error
    }
  }

  #choose(option) {
    if (!option) return
    if (option.hasAttribute("data-create")) {
      this.#closeList()
      this.#openCreate(option.dataset.query || "")
      return
    }

    const { customerId, customerType, customerName, gridLabel } = option.dataset
    this.customerIdTarget.value = customerId
    this.customerIdTarget.dataset.customerType = customerType
    this.pickedTarget.replaceChildren(option.querySelector("template[data-card]").content.cloneNode(true))
    this.inputTarget.value = ""
    this.#closeList()
    this.#showCard(true)
    this.#fillAddresses(JSON.parse(option.dataset.addresses || "[]"))
    document.querySelectorAll("[data-estimate-field='customer']").forEach(element => { element.textContent = customerName })
    document.querySelectorAll("[data-estimate-field='grid']").forEach(element => { element.textContent = gridLabel })

    // A hidden field fires no change of its own: the autosave learns the estimate changed from this one
    this.customerIdTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.dispatch("selected", { detail: { id: customerId, customerType, name: customerName,
      paymentSettings: JSON.parse(option.dataset.paymentSettings || "{}") } })
  }

  // Same rules as the server: the customer's record address first, then its addresses, the default ones selected
  #fillAddresses(addresses) {
    const fill = (select, defaultKey) => {
      const fallback = new Option(this.mainAddressLabelValue, "")
      select.replaceChildren(fallback, ...addresses.map(address =>
        new Option(address.label, address.id, false, Boolean(address[defaultKey]))))
      select.disabled = false
    }

    if (this.hasBillingTarget) fill(this.billingTarget, "billing_default")
    if (this.hasShippingTarget) fill(this.shippingTarget, "shipping_default")
  }

  #openCreate(name) {
    const dialog = this.#dialog()
    if (!dialog) return

    this.createNameTarget.value = name
    dialog.showModal()
    this.createNameTarget.focus()
  }

  #dialog() {
    return document.getElementById(this.dialogIdValue)
  }

  #parseOption(html) {
    const template = document.createElement("template")
    template.innerHTML = html.trim()
    return template.content.querySelector("[role='option']")
  }

  #options() {
    return [...this.listTarget.querySelectorAll("[role='option']")]
  }

  #activate(index) {
    const options = this.#options()
    this._active = options.length ? index : -1
    options.forEach((option, position) => option.setAttribute("aria-selected", String(position === this._active)))

    const active = options[this._active]
    if (active) {
      this.inputTarget.setAttribute("aria-activedescendant", active.id)
      active.scrollIntoView({ block: "nearest" })
    } else {
      this.inputTarget.removeAttribute("aria-activedescendant")
    }
  }

  #closeList() {
    this.listTarget.hidden = true
    this._active = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  #showCard(shown) {
    this.comboTarget.hidden = shown
    this.hintTarget.hidden = shown
    this.pickedTarget.hidden = !shown
  }
}
